import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  OrderPaymentTransactionCreate,
  OrderPaymentTransactionResponse,
} from "@/api";

import FormInput from "@/framework/forms/FormInput";
import { PaymentTransactionService } from "../../common/service/orderPaymentsService";
import PaymentTransactionsTable from "../../common/PaymentTransactionTable";

interface PageMessageProps {
  children: React.ReactNode;
  variant?: "normal" | "error";
}

function PageMessage({
  children,
  variant = "normal",
}: PageMessageProps) {
  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 lg:px-6">
      <div
        className={
          variant === "error"
            ? "rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700"
            : "rounded-xl border bg-white p-8 text-sm text-gray-500"
        }
      >
        {children}
      </div>
    </div>
  );
}

export default function PaymentTransactionsPage() {
  const { orderId } = useParams();

  const parsedOrderId = Number(orderId);

  const [transactions, setTransactions] =
    useState<OrderPaymentTransactionResponse[]>([]);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  /*
   * --------------------------------------------------
   * FORM STATE
   * --------------------------------------------------
   */

  const [transactionType, setTransactionType] =
    useState("PAYMENT_IN");

  const [transactionDate, setTransactionDate] =
    useState("");

  const [transactionAmount, setTransactionAmount] =
    useState<number | "">("");

  const [transactionReason, setTransactionReason] =
    useState("");

  const [transactionReference, setTransactionReference] =
    useState("");

  /*
   * --------------------------------------------------
   * LOAD TRANSACTIONS
   * --------------------------------------------------
   */

  const loadTransactions = useCallback(async () => {
    if (!Number.isFinite(parsedOrderId)) {
      setError("Invalid order ID.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response =
        await PaymentTransactionService.list(
          parsedOrderId
        );

      setTransactions(response);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to load payment transactions."
      );
    } finally {
      setLoading(false);
    }
  }, [parsedOrderId]);

  useEffect(() => {
    void loadTransactions();
  }, [loadTransactions]);

  /*
   * --------------------------------------------------
   * RESET FORM
   * --------------------------------------------------
   */

  const resetForm = () => {
    setTransactionType("PAYMENT_IN");
    setTransactionDate("");
    setTransactionAmount("");
    setTransactionReason("");
    setTransactionReference("");
  };

  /*
   * --------------------------------------------------
   * VALIDATION
   * --------------------------------------------------
   */

  const validate = (): boolean => {
    if (!transactionDate) {
      setError("Transaction date is required.");
      return false;
    }

    if (
      transactionAmount === "" ||
      Number(transactionAmount) <= 0
    ) {
      setError(
        "Transaction amount must be greater than zero."
      );
      return false;
    }

    return true;
  };

  /*
   * --------------------------------------------------
   * CREATE PAYMENT
   * --------------------------------------------------
   */

  const handleCreate = async () => {
    setError(null);

    if (!validate()) {
      return;
    }

    if (!Number.isFinite(parsedOrderId)) {
      setError("Invalid order ID.");
      return;
    }

    try {
      setSaving(true);

      let transactionReferenceValue:
        | Record<string, unknown>
        | null = null;

      if (transactionReference.trim()) {
        try {
          transactionReferenceValue =
            JSON.parse(transactionReference);
        } catch {
          transactionReferenceValue = {
            reference:
              transactionReference.trim(),
          };
        }
      }

      const payload:
        OrderPaymentTransactionCreate = {

        installment_id: null,

        currency: "INR",

        transaction_type:
          transactionType,

        transaction_date:
          transactionDate,

        transaction_amount:
          Number(transactionAmount),

        transaction_reason:
          transactionReason.trim() || null,

        transaction_reference:
          transactionReferenceValue,
      };

      const created =
        await PaymentTransactionService.create(
          payload
        );

      /*
       * Add the server response to the table.
       */
      setTransactions((current) => [
        ...current,
        created,
      ]);

      resetForm();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to create payment transaction."
      );
    } finally {
      setSaving(false);
    }
  };

  /*
   * --------------------------------------------------
   * INVALID ORDER
   * --------------------------------------------------
   */

  if (!Number.isFinite(parsedOrderId)) {
    return (
      <PageMessage variant="error">
        Invalid order ID.
      </PageMessage>
    );
  }

  /*
   * --------------------------------------------------
   * LOADING
   * --------------------------------------------------
   */

  if (loading) {
    return (
      <PageMessage>
        Loading payment transactions...
      </PageMessage>
    );
  }

  /*
   * --------------------------------------------------
   * PAGE
   * --------------------------------------------------
   */

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 lg:px-6">
      {/* ==================================================
          HEADER
      ================================================== */}

      <div className="mb-6">
        <h1 className="text-2xl font-semibold">
          Payment Transactions
        </h1>

        <p className="mt-1 text-sm text-gray-500">
          Order #{parsedOrderId}
        </p>
      </div>

      {/* ==================================================
          ERROR
      ================================================== */}

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ==================================================
          NEW PAYMENT
      ================================================== */}

      <section className="rounded-xl border bg-white">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            Record Payment
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Add a payment transaction for this order.
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-2 lg:grid-cols-5">
            {/* TRANSACTION TYPE */}

            <div>
              <FormInput
                label="Transaction Type"
                value={transactionType}
                required
                placeholder="PAYMENT_IN"
                onValueChange={(value) => {
                  setTransactionType(value);
                }}
              />
            </div>

            {/* DATE */}

            <div>
              <FormInput
                label="Transaction Date"
                value={transactionDate}
                required
                placeholder="YYYY-MM-DD"
                onValueChange={(value) => {
                  setTransactionDate(value);
                }}
              />
            </div>

            {/* AMOUNT */}

            <div>
              <FormInput
                label="Amount"
                value={transactionAmount}
                required
                validation="numeric"
                placeholder="Amount"
                onValueChange={(value) => {
                  setTransactionAmount(
                    value === ""
                      ? ""
                      : Number(value)
                  );
                }}
              />
            </div>

            {/* REASON */}

            <div>
              <FormInput
                label="Reason"
                value={transactionReason}
                placeholder="Payment received"
                onValueChange={(value) => {
                  setTransactionReason(value);
                }}
              />
            </div>

            {/* ACTION */}

            <div>
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleCreate()}
                className="w-full rounded-md bg-black px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving
                  ? "Saving..."
                  : "Add Payment"}
              </button>
            </div>
          </div>

          {/* REFERENCE */}

          <div className="mt-4">
            <FormInput
              label="Transaction Reference"
              value={transactionReference}
              placeholder="UTR / Gateway Transaction ID"
              onValueChange={(value) => {
                setTransactionReference(value);
              }}
            />
          </div>
        </div>
      </section>

      {/* ==================================================
          PAYMENT TRANSACTIONS
      ================================================== */}

      <section className="mt-6 rounded-xl border bg-white">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            Payment History
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            All payment transactions recorded against
            this order.
          </p>
        </div>

        <div className="p-6">
          <PaymentTransactionsTable
            transactions={transactions}
          />
        </div>
      </section>
    </div>
  );
}